import { api } from '../services/api';

/**
 * Carries "I came here to create a ranking" across signup.
 *
 * The click happens on the landing page, where there is no account yet — so the
 * intent is parked in localStorage and attached to the subscriber the moment
 * one exists (see AuthContext). It survives the magic-link round trip because
 * the link opens in the same browser; if it doesn't (link opened on another
 * device), the intent is simply lost, which is fine — this drives outreach, not
 * behaviour.
 */

const KEY = 'bt_signup_intent';

/** Which of the landing page's CTAs was clicked. */
export type SignupIntentCta = 'nav' | 'hero' | 'final' | 'sticky';

interface StoredIntent {
  intent: 'create_ranking';
  cta: SignupIntentCta;
  clickedAt: string;
}

export function rememberSignupIntent(cta: SignupIntentCta): void {
  try {
    const payload: StoredIntent = {
      intent: 'create_ranking',
      cta,
      clickedAt: new Date().toISOString(),
    };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Private mode or full storage — not worth failing the click over.
  }
}

/**
 * Send any parked intent to the backend and clear it. Safe to call on every
 * subscriber load: it no-ops when nothing is parked, and the backend keeps
 * only the first intent per subscriber.
 */
export async function flushSignupIntent(): Promise<void> {
  let stored: StoredIntent | null = null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    stored = JSON.parse(raw) as StoredIntent;
  } catch {
    localStorage.removeItem(KEY);
    return;
  }

  if (stored?.intent !== 'create_ranking') {
    localStorage.removeItem(KEY);
    return;
  }

  try {
    const response = await api.auth.recordSignupIntent(stored);
    // Keep it parked if the call failed, so the next load retries.
    if (response.success) localStorage.removeItem(KEY);
  } catch {
    // Offline or backend down — retry on the next subscriber load.
  }
}
